import { useQuery } from '@tanstack/react-query'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import {
  adPerformanceApi,
  AdPerfBreakdown,
  AdPerfRange,
} from 'src/services/adPerformanceApi'

// All Ad Performance queries share one key prefix so a refetch after a sync
// can invalidate them together.
const KEY = 'adPerformance'

const enabled = (r?: AdPerfRange) =>
  !!getSessionIdFromLocalStorage() && (!r || !!r.accountId)

export const useAdPerfAccounts = () =>
  useQuery({
    enabled: enabled(),
    queryFn: adPerformanceApi.accounts,
    queryKey: [KEY, 'accounts'],
    // While an account is still on its first sync, check back every 30s.
    refetchInterval: (q) =>
      q.state.data?.some((a) => !a.synced) ? 30000 : false,
  })

export const useAdPerfOverview = (r: AdPerfRange) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.overview(r),
    queryKey: [KEY, 'overview', r],
  })

export const useAdPerfTrends = (r: AdPerfRange, metric: string) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.trends(r, metric),
    queryKey: [KEY, 'trends', r, metric],
  })

export const useAdPerfActiveTimes = (r: AdPerfRange, metric: string) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.activeTimes(r, metric),
    queryKey: [KEY, 'activeTimes', r, metric],
  })

export const useAdPerfBreakdown = (
  r: AdPerfRange,
  dim: AdPerfBreakdown['dim']
) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.breakdown(r, dim),
    queryKey: [KEY, 'breakdown', r, dim],
  })

export const useAdPerfCampaigns = (r: AdPerfRange) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.campaigns(r),
    queryKey: [KEY, 'campaigns', r],
  })

export const useAdPerfCreatives = (r: AdPerfRange, format?: string) =>
  useQuery({
    enabled: enabled(r),
    queryFn: () => adPerformanceApi.creatives(r, format),
    queryKey: [KEY, 'creatives', r, format],
  })

export const useAdPerfAdDetail = (r: AdPerfRange, adId: string | null) =>
  useQuery({
    enabled: enabled(r) && !!adId,
    queryFn: () => adPerformanceApi.adDetail(r, adId as string),
    queryKey: [KEY, 'adDetail', r, adId],
  })

export const useAdPerfSmartInsights = (accountId: string) =>
  useQuery({
    enabled: enabled() && !!accountId,
    queryFn: () => adPerformanceApi.smartInsights(accountId),
    queryKey: [KEY, 'smartInsights', accountId],
  })
